/**
 * Countdown timer for announcement bar slides.
 * Uses data-part attributes (not Component refs) so it works nested
 * inside announcement-bar-component.
 */
class AnnouncementCountdown extends HTMLElement {
  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {number} */
  #endMs = 0;

  connectedCallback() {
    const endValue = this.dataset.end?.trim();
    if (!endValue) {
      this.#complete();
      return;
    }

    this.#endMs = this.#parseEndDate(endValue);

    if (Number.isNaN(this.#endMs)) {
      console.warn('[announcement-countdown] Invalid end date:', endValue);
      this.#complete();
      return;
    }

    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    this.#clear();
  }

  /**
   * @param {string} value
   * @returns {number}
   */
  #parseEndDate(value) {
    const normalized = value.trim().replace(' ', 'T');
    const parsed = Date.parse(normalized);

    if (!Number.isNaN(parsed)) return parsed;

    // Fallback: YYYY-MM-DD HH:MM[:SS] or YYYY-MM-DDTHH:MM[:SS]
    const match = normalized.match(
      /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?/
    );

    if (!match) return Number.NaN;

    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const hours = Number(match[4]);
    const minutes = Number(match[5]);
    const seconds = Number(match[6] ?? 0);

    return new Date(year, month, day, hours, minutes, seconds).getTime();
  }

  #clear() {
    if (this.#interval !== undefined) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  #tick() {
    const remaining = this.#endMs - Date.now();

    if (remaining <= 0) {
      this.#complete();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#setValue('days', days);
    this.#setValue('hours', hours);
    this.#setValue('minutes', minutes);
    this.#setValue('seconds', seconds);

    const daysUnit = this.querySelector('[data-part="days-unit"]');
    if (daysUnit instanceof HTMLElement) {
      daysUnit.hidden = days <= 0 && this.dataset.hideEmptyDays === 'true';
    }

    const daysSep = this.querySelector('[data-part="days-sep"]');
    if (daysSep instanceof HTMLElement) {
      daysSep.hidden = daysUnit instanceof HTMLElement && daysUnit.hidden;
    }
  }

  /**
   * @param {string} part
   * @param {number} value
   */
  #setValue(part, value) {
    const el = this.querySelector(`[data-part="${part}"]`);
    if (!(el instanceof HTMLElement)) return;

    el.textContent = String(value).padStart(2, '0');
  }

  #complete() {
    this.#clear();
    this.setAttribute('data-complete', 'true');

    if (this.dataset.hideWhenComplete === 'true') {
      const slide = this.closest('slideshow-slide');
      if (slide instanceof HTMLElement) {
        slide.hidden = true;
        slide.setAttribute('aria-hidden', 'true');

        const bar = this.closest('announcement-bar-component');
        if (bar && 'current' in bar && typeof bar.current === 'number') {
          bar.current = bar.current;
        }
      } else {
        this.hidden = true;
      }
      return;
    }

    this.#setValue('days', 0);
    this.#setValue('hours', 0);
    this.#setValue('minutes', 0);
    this.#setValue('seconds', 0);
  }
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}
